import React, { useEffect, useMemo, useState } from 'react';
import { getOpenSlots } from '../api/client';
import { formatSlotTime } from '../utils/serviceApplications';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const dateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const displayDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};

export function DateField({ value, onChange, isEnabled, hasMark, placeholder = 'dd/mm/yyyy' }) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => startOfToday());

  const choose = (key) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div className="date-field">
      <button
        type="button"
        className={`date-field-input${value ? ' has-value' : ''}${open ? ' open' : ''}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label="Appointment date"
      >
        <span>{displayDate(value) || placeholder}</span>
        <i className="fas fa-calendar-days" aria-hidden="true" />
      </button>
      {open ? (
        <MonthGrid
          cursor={cursor}
          onCursor={setCursor}
          selected={value}
          onSelect={choose}
          isEnabled={isEnabled}
          hasMark={hasMark}
        />
      ) : null}
    </div>
  );
}

export function MonthGrid({ cursor, onCursor, selected, onSelect, isEnabled, hasMark }) {
  const today = startOfToday();
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const lead = monthStart.getDay();
  const count = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [...Array(lead).fill(null)];
  for (let day = 1; day <= count; day += 1) {
    cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), day));
  }
  const canPrev = cursor.getFullYear() > today.getFullYear() || cursor.getMonth() > today.getMonth();
  const title = cursor.toLocaleDateString('en-PK', { month: 'long', year: 'numeric' });

  return (
    <div className="slot-calendar">
      <div className="slot-calendar-head">
        <button type="button" onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} disabled={!canPrev} aria-label="Previous month">
          <i className="fas fa-chevron-left" />
        </button>
        <strong>{title}</strong>
        <button type="button" onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Next month">
          <i className="fas fa-chevron-right" />
        </button>
      </div>
      <div className="slot-calendar-week">
        {WEEKDAYS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="slot-calendar-grid">
        {cells.map((date, index) => {
          if (!date) return <span key={`empty-${index}`} className="slot-day empty" />;
          const key = dateKey(date);
          const enabled = isEnabled(date, key);
          return (
            <button
              key={key}
              type="button"
              className={`slot-day${enabled ? ' open' : ''}${selected === key ? ' selected' : ''}${hasMark?.(key) ? ' marked' : ''}`}
              disabled={!enabled}
              onClick={() => onSelect(key)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SlotPicker = ({ serviceType, date, time, onChange }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getOpenSlots(serviceType)
      .then((data) => {
        if (!cancelled) setDays(data.dates || []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load open appointment times.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [serviceType]);

  const byDate = useMemo(() => {
    const map = new Map();
    days.forEach((day) => map.set(day.date, day.times || []));
    return map;
  }, [days]);

  const today = startOfToday();
  const times = date ? byDate.get(date) || [] : [];

  const chooseDate = (nextDate) => {
    const nextTimes = byDate.get(nextDate) || [];
    onChange({ date: nextDate, time: nextTimes.includes(time) ? time : '' });
  };

  return (
    <div className="slot-picker">
      <span className="slot-picker-label">Appointment date *</span>
      {loading ? <p className="form-help">Loading open days…</p> : null}
      {error ? <p className="form-help">{error}</p> : null}
      <DateField
        value={date}
        onChange={chooseDate}
        isEnabled={(value) => value >= today}
        hasMark={(key) => byDate.has(key)}
      />
      {date ? (
        <div className="slot-times" role="group" aria-label="Available times">
          <span className="slot-picker-label">Available time *</span>
          {times.length === 0 ? (
            <p className="form-help">No open times on this day. Pick another date.</p>
          ) : (
            times.map((slot) => (
              <button
                key={slot}
                type="button"
                className={time === slot ? 'active' : ''}
                onClick={() => onChange({ date, time: slot })}
              >
                {formatSlotTime(slot)}
              </button>
            ))
          )}
        </div>
      ) : (
        <p className="form-help">Open the date field and choose a future day. Days with a mark have open times.</p>
      )}
    </div>
  );
};

export default SlotPicker;
